// apps/client/src/hooks/reports/resolveReportAccess.ts
//
// PURE report-access resolver - the single composition point for the report
// availability logic that was previously re-implemented independently across
// four nav/guard sites. No React, no I/O; every function is a deterministic
// map from the caller-supplied access context to a decision.
//
// Two families of output:
//
//   1. CANONICAL resolution - `resolveReportCatalogue()` returns, per report,
//      `{ unlockedForTenant, grantedToUser, lockReason }` composed from
//      role ∩ (tier via tierHasFeature OR featureFlags additive override OR
//      betaFeatures) ∩ per-user allowedReports. This is the corrected,
//      tier-aware view the Team Management V2 page will render. It is NOT wired
//      to anything in PR A1.
//
//   2. LEGACY-FAITHFUL selectors - `firstAllowedReportPath()`,
//      `sidebarReportsLinkTarget()`, `topBarPortfolioItems()` and
//      `isReportAllowedForUser()` reproduce the EXACT current behaviour of the
//      four consumer sites, byte-for-byte, so PR A2 can swap them in with the
//      committed golden fixture as the no-regression proof.
//
// The equivalence to today's code is proven by the golden jest suite, which
// checks these selectors against verbatim copies of each site's logic.

import { tierHasFeature, type Feature, type SubscriptionTier } from '../../config/subscriptionTiers';
import {
  ADMIN_REPORT_ROUTE_ROLES,
  REPORT_REGISTRY,
  DEFAULT_REPORT_PATH,
  NO_REACHABLE_REPORT_PATH,
  SIDEBAR_DEFAULT_REPORT_PATH,
  SIDEBAR_NON_BROKER_REPORT_PATH,
  type ReportDefinition,
  type ReportSlug,
} from '../../config/reportRegistry';

/** The complete access context read by every report site. */
export interface ReportAccessContext {
  /** Auth-claim role. */
  role: string | null | undefined;
  /** Resolved subscription tier (plus | pro | enterprise), or null when unknown. */
  tier: SubscriptionTier | null | undefined;
  /** `companyData.featureFlags`: additive boolean grants + tri-state strings. */
  featureFlags: Record<string, unknown>;
  /** `companyData.betaFeatures`. */
  betaFeatures: string[];
  /** `userData.allowedReports`: per-user broker grant list (undefined = unrestricted). */
  allowedReports: string[] | null | undefined;
  /** `userData.hideCommissions`. Part of the canonical context; does not gate report availability. */
  hideCommissions: boolean;
  /** Whether the tenant owns acquired books (derived from templates in AppLayout). */
  hasAcquiredBooks: boolean;
}

export type LockReason = 'tier' | 'flag-off' | 'not-granted' | null;

export interface ReportResolution {
  slug: ReportSlug;
  label: string;
  path: string;
  /** Report is available to the tenant (tier / flag / beta satisfied). */
  unlockedForTenant: boolean;
  /** Report is available to this specific user (tenant unlock ∩ per-user grant). */
  grantedToUser: boolean;
  /** Why the report is not granted, or null when granted. */
  lockReason: LockReason;
}

export interface NavItem {
  label: string;
  to: string;
  feature?: Feature;
}

// Roles that carry per-user `allowedReports` controls (the broker family, as
// defined by TeamManagementPage.tsx: broker, broker_assistant, credit_analyst).
const BROKER_FAMILY_ROLES = new Set(['broker', 'broker_assistant', 'credit_analyst']);

/**
 * Whether a NON-broker-family role's per-user `allowedReports` list constrains
 * report access. Only `office_admin` qualifies.
 *
 * INERTNESS (load-bearing): this returns false unless a NON-EMPTY list is
 * actually present. No office_admin carries `allowedReports` today (P-CENSUS
 * control 3) and only the flag-gated setter can write one, so every existing
 * office_admin takes the `false` branch and every consumer below is
 * byte-identical to its pre-change behaviour.
 *
 * The empty-list check is not merely an optimisation. Several consumers treat
 * "constrained" as a mode rather than a filter - `sidebarReportsLinkTarget`
 * picks a DIFFERENT default destination for a constrained role - so a predicate
 * that keyed on the role alone would change where an ordinary office_admin
 * lands the moment this shipped, with no grant list involved.
 */
export function officeAdminReportGrantsApply(
  role: string | null | undefined,
  allowedReports: string[] | null | undefined,
): boolean {
  return role === 'office_admin' && Array.isArray(allowedReports) && allowedReports.length > 0;
}

// ---------------------------------------------------------------------------
// Canonical resolution
// ---------------------------------------------------------------------------

/** Mirrors UnifiedTopBar.hasFeature: additive per-company grant OR tier rank. */
function featureUnlockedByTierOrGrant(feature: Feature, ctx: ReportAccessContext): boolean {
  if (ctx.featureFlags[feature] === true) return true;
  if (!ctx.tier) return false;
  return tierHasFeature(ctx.tier, feature);
}

function resolveTenantUnlock(def: ReportDefinition, ctx: ReportAccessContext): { unlocked: boolean; lockReason: LockReason } {
  // Tri-state per-tenant flag (book-trend): unlocks only on exactly 'enabled'.
  if (def.triStateFlag) {
    if (ctx.featureFlags[def.triStateFlag] !== 'enabled') {
      return { unlocked: false, lockReason: 'flag-off' };
    }
  }
  // Boolean per-tenant flag (commission-settings): must be === true.
  if (def.requiredFlag) {
    if (ctx.featureFlags[def.requiredFlag] !== true) {
      return { unlocked: false, lockReason: 'flag-off' };
    }
  }
  // Tier feature gate, with the acquired-books escape hatch.
  if (def.requiredFeature) {
    const byFeature =
      featureUnlockedByTierOrGrant(def.requiredFeature, ctx) ||
      ctx.betaFeatures.includes(def.requiredFeature);
    const byAcquired = def.unlockedByAcquiredBooks === true && ctx.hasAcquiredBooks === true;
    if (!byFeature && !byAcquired) {
      return { unlocked: false, lockReason: 'tier' };
    }
  }
  return { unlocked: true, lockReason: null };
}

/**
 * Whether this user's per-user grant list allows the slug, ignoring tier.
 * Verbatim mirror of hooks/useAccessRestrictions.ts `hasReportAccess`:
 * an absent / empty `allowedReports` means unrestricted (returns true).
 */
export function isReportAllowedForUser(allowedReports: string[] | null | undefined, slug: string): boolean {
  if (!allowedReports || !Array.isArray(allowedReports) || allowedReports.length === 0) return true;
  return allowedReports.includes(slug);
}

function resolveUserGrant(
  def: ReportDefinition,
  ctx: ReportAccessContext,
  tenantUnlocked: boolean,
  tenantLockReason: LockReason,
): { granted: boolean; lockReason: LockReason } {
  if (!tenantUnlocked) return { granted: false, lockReason: tenantLockReason };

  // Broker-family users are constrained by per-user allowedReports over the 12
  // broker-grantable reports; an office_admin carrying a non-empty grant list is
  // constrained over the union (broker-grantable + the 3 valuation reports).
  // Every other role, and an unrestricted office_admin, sees all tenant-unlocked
  // reports exactly as before.
  const isBrokerFamily = BROKER_FAMILY_ROLES.has(ctx.role ?? '');
  const constrained = isBrokerFamily
    ? def.brokerGrantable
    : officeAdminReportGrantsApply(ctx.role, ctx.allowedReports) &&
      (def.brokerGrantable || def.adminGrantable === true);
  if (!constrained) {
    return { granted: true, lockReason: null };
  }
  const granted = isReportAllowedForUser(ctx.allowedReports, def.slug);
  return granted ? { granted: true, lockReason: null } : { granted: false, lockReason: 'not-granted' };
}

/**
 * Canonical, tier-aware resolution of all 12 reports for a given context.
 * This is the corrected view; it is NOT what any legacy nav site computes today.
 */
export function resolveReportCatalogue(ctx: ReportAccessContext): ReportResolution[] {
  return REPORT_REGISTRY.map((def) => {
    const { unlocked, lockReason: tenantLockReason } = resolveTenantUnlock(def, ctx);
    const { granted, lockReason } = resolveUserGrant(def, ctx, unlocked, tenantLockReason);
    return {
      slug: def.slug,
      label: def.label,
      path: def.path,
      unlockedForTenant: unlocked,
      grantedToUser: granted,
      lockReason,
    };
  });
}

// ---------------------------------------------------------------------------
// Legacy-faithful selectors (byte-for-byte reproductions for PR A2)
// ---------------------------------------------------------------------------

// Guard path map - the 11 slugs present in routes/guards/reportAccessUtils.ts
// (book-trend absent), derived from the registry's `inGuardPathMap` flag.
const GUARD_SLUG_TO_PATH: Record<string, string> = Object.fromEntries(
  REPORT_REGISTRY.filter((d) => d.inGuardPathMap).map((d) => [d.slug, d.path]),
);

// Sidebar Reports-link path map - the 11 slugs present in the MorphingSidebar
// inline map (book-trend absent), derived from `sidebarFirstReportEligible`.
const SIDEBAR_SLUG_TO_PATH: Record<string, string> = Object.fromEntries(
  REPORT_REGISTRY.filter((d) => d.sidebarFirstReportEligible).map((d) => [d.slug, d.path]),
);

// Top-bar granular gate map - the paths present in UnifiedTopBar.pathToReportSlug,
// derived from `inTopBarGating`. (The live map also carries approved-vs-settled,
// which is not a grantable report and never appears in the Portfolio items this
// selector builds, so its absence here is behaviourally inert.)
const TOPBAR_PATH_TO_SLUG: Record<string, string> = Object.fromEntries(
  REPORT_REGISTRY.filter((d) => d.inTopBarGating).map((d) => [d.path, d.slug]),
);

/**
 * Redirect target for the three route guards (RequireReportAccess,
 * RequireCommissionAccess, RequireTenantReport) when the user may not open the
 * report they asked for.
 *
 * REDIRECT-LOOP FIX. This previously read
 * `GUARD_SLUG_TO_PATH[allowedReports[0]] || DEFAULT_REPORT_PATH`, which looked
 * at the FIRST grant only and fell back to `/reports/settlement-age`. Both legs
 * were unsound, because settlement-age is ITSELF wrapped in
 * RequireReportAccess (AppRoutes.tsx:422):
 *
 *   - first-slug-only: a grant list whose first slug has no guard path but
 *     whose later slugs do (e.g. `['book-trend', 'loans']`) fell to
 *     settlement-age, which the same guard then refused, redirecting to
 *     settlement-age again - an infinite loop. This is a LIVE bug for a broker
 *     granted book-trend first, not only for the new admin-tier slugs; the
 *     fixture matrix has carried exactly that list since PR A1.
 *   - unmapped-only lists: `['trail-forecast']` has no mapped slug at all, so
 *     no scan can help and the fallback itself must be reachable.
 *
 * So: scan the WHOLE list for the first slug that maps to a route, and when
 * none does, leave the report area entirely for a path no guard wraps.
 *
 * The absent/empty case still returns DEFAULT_REPORT_PATH and is unchanged: an
 * unrestricted user is never redirected by these guards in the first place
 * (RequireReportAccess.tsx renders the Outlet on an empty list), so that leg
 * cannot loop.
 */
export function firstAllowedReportPath(allowedReports?: string[] | null): string {
  if (!allowedReports || !Array.isArray(allowedReports) || allowedReports.length === 0) {
    return DEFAULT_REPORT_PATH;
  }
  for (const slug of allowedReports) {
    const path = GUARD_SLUG_TO_PATH[slug];
    if (path) return path;
  }
  return NO_REACHABLE_REPORT_PATH;
}

/**
 * Verbatim reproduction of the MorphingSidebar Reports NavLink target + its
 * outer visibility guard. Returns null when the Reports link is hidden (a
 * marketing user with no grants), otherwise the destination path.
 */
export function sidebarReportsLinkTarget(
  role: string | null | undefined,
  allowedReports: string[] | null | undefined,
): string | null {
  const isBrokerFamilyRole = role === 'broker' || role === 'broker_assistant' || role === 'credit_analyst';
  const isMarketingRole = role === 'marketing';

  // Outer visibility guard (MorphingSidebar line ~439).
  if (isMarketingRole && (!Array.isArray(allowedReports) || allowedReports.length === 0)) {
    return null;
  }

  // NavLink `to` IIFE (MorphingSidebar lines ~441-464).
  if (!isBrokerFamilyRole && !isMarketingRole) return SIDEBAR_NON_BROKER_REPORT_PATH;
  const allowed = allowedReports;
  if (!Array.isArray(allowed) || allowed.length === 0) return SIDEBAR_DEFAULT_REPORT_PATH;
  const firstReportSlug = allowed.find((s: string) => s !== 'commission-settings') ?? allowed[0];
  return SIDEBAR_SLUG_TO_PATH[firstReportSlug] || SIDEBAR_DEFAULT_REPORT_PATH;
}

/**
 * Verbatim reproduction of UnifiedTopBar.hasReportAccess(path): the granular
 * broker filter over the Reports mega-menu. Only broker / broker_assistant are
 * constrained here (credit_analyst is NOT broker-family in the top bar).
 */
function topBarHasReportAccess(
  role: string | null | undefined,
  allowedReports: string[] | null | undefined,
  path: string,
): boolean {
  const isBrokerFamilyRole = role === 'broker' || role === 'broker_assistant';
  // An office_admin with a non-empty grant list is filtered by the same granular
  // map. Note this deliberately does NOT widen `isBrokerFamilyRole` itself: that
  // flag also removes Trail Run-Off, Trail Book Valuation, Trail Forecast, RCTI
  // Cashflow and the entire Offices section from the mega-menu, none of which an
  // office_admin should lose.
  if (!isBrokerFamilyRole && !officeAdminReportGrantsApply(role, allowedReports)) return true;
  const allowed = allowedReports;
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  const slug = TOPBAR_PATH_TO_SLUG[path];
  if (!slug) return true; // non-gated paths always visible
  return allowed.includes(slug);
}

/**
 * Verbatim reproduction of the UnifiedTopBar "Portfolio" sub-section item list
 * (subscriptionTiers gating shown as a lock at render time, not removed here),
 * after the granular broker `filterItems(hasReportAccess)` pass.
 */
export function topBarPortfolioItems(ctx: ReportAccessContext): NavItem[] {
  const role = ctx.role ?? '';
  const grantedFeatures = ctx.featureFlags;

  const items: NavItem[] = [
    { label: 'Loan Portfolio', to: '/reports/loans' },
    { label: 'Customer Portfolio', to: '/reports/customer-portfolio', feature: 'customer_portfolio' },
    // Beta AND an admin report role: the referral-partners route admits only
    // ADMIN_REPORT_ROUTE_ROLES, so offering it to anyone else redirects to "/".
    ...(ctx.betaFeatures.includes('referral_partners') && ADMIN_REPORT_ROUTE_ROLES.includes(role)
      ? [{ label: 'Referral Partners', to: '/reports/referral-partners', feature: 'referral_partners' as Feature }]
      : []),
    { label: 'Client Retention', to: '/reports/refinance-intelligence', feature: 'refinance_intelligence' },
    ...(ctx.hasAcquiredBooks || grantedFeatures.refinance_intelligence === true
      ? [{ label: 'Acquired Books', to: '/reports/acquired-books', feature: 'refinance_intelligence' as Feature }]
      : []),
    ...(['administrator', 'super_admin'].includes(role)
      ? [{ label: 'Broker Loan Books', to: '/reports/broker-loan-books', feature: 'broker_loan_books' as Feature }]
      : []),
    ...(['administrator', 'super_admin'].includes(role) && (grantedFeatures as Record<string, unknown>).book_value_report === 'enabled'
      ? [{ label: 'Book Value Report', to: '/reports/book-value' }]
      : []),
    ...(() => {
      const flagOk = (grantedFeatures as Record<string, unknown>).broker_book_trend_report === 'enabled';
      if (!flagOk) return [];
      if (!['administrator', 'super_admin', 'office_admin', 'broker', 'broker_assistant'].includes(role)) {
        return [];
      }
      if (role === 'broker' || role === 'broker_assistant') {
        const allowed = ctx.allowedReports;
        if (!Array.isArray(allowed) || !allowed.includes('book-trend')) return [];
      }
      return [{ label: 'Book Trend Report', to: '/reports/book-trend' }];
    })(),
  ];

  return items.filter((item) => topBarHasReportAccess(ctx.role, ctx.allowedReports, item.to));
}
